/* eslint-disable @typescript-eslint/no-unnecessary-condition */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-redundant-type-constituents */
/* eslint-disable @typescript-eslint/no-explicit-any */

/**
 * This is the entry point for Feature Hub App integration
 */
import React from 'react';
import type {
  FeatureServices,
  FeatureAppDefinition,
  FeatureServiceProviderDefinition,
} from '@feature-hub/core';
import type { Logger } from '@feature-hub/logger';
import type {
  ContentServiceV1,
  TrackingService,
  RenderModeServiceV1,
  LocaleServiceV1,
  AsyncSsrManager,
  SerializedStateManager,
  VueFormatterServiceInterfaceV1,
  AudiFootnoteReferenceServiceInterfaceV3,
  AudiFootnoteRefernceServiceScopeManagerInterfaceV3,
} from './feature-service-types';
import type { ReactFeatureApp } from '@feature-hub/react';
import { FootnoteContextProvider, InViewContextProvider } from '@oneaudi/feature-app-utils';
import { UniversalEditorProvider } from '@oneaudi/falcon-tools';
import FeatureApp from './FeatureApp';
import { ContextProvider } from './context';
import { getDesignLibrary } from '../alpha-helper';
import type { AudiFootnoteReferenceServiceInterface } from '@oneaudi/footnote-reference-service';

export interface FeatureServiceDependencies extends FeatureServices {
  readonly 's2:logger'?: Logger;
  readonly 'audi-content-service'?: ContentServiceV1;
  readonly 'dbad:audi-vue-formatter-service'?: VueFormatterServiceInterfaceV1;
  readonly 'audi-footnote-reference-service': FeatureServiceProviderDefinition<AudiFootnoteReferenceServiceInterface>;
  readonly 'locale-service': LocaleServiceV1;
  readonly 'audi-tracking-service'?: TrackingService;
  readonly 's2:async-ssr-manager'?: AsyncSsrManager;
  readonly 's2:serialized-state-manager'?: SerializedStateManager;
  readonly 'audi-render-mode-service'?: RenderModeServiceV1;
}

export type SSRContent = any | undefined;

const featureAppDefinition: FeatureAppDefinition<ReactFeatureApp, FeatureServiceDependencies> = {
  dependencies: {
    featureServices: {
      's2:logger': '^1.0.0',
      'locale-service': '^1.0.0',
      'audi-footnote-reference-service': '^3.0.0',
      'dbad:audi-vue-formatter-service': '^1.0.0',
    },
    externals: {
      react: '^16.13.0 || ^17.0.2 || ^18.x.x',
      'styled-components': '*',
      ...getDesignLibrary({ unifiedWebCommonVersion: '^1.8.0' }),
    },
  },

  optionalDependencies: {
    featureServices: {
      'audi-content-service': '^1.0.0',
      's2:logger': '^1.0.0',
      's2:async-ssr-manager': '^1.0.0',
      's2:serialized-state-manager': '^1.0.0',
      'audi-render-mode-service': '^1.0.0',
      'audi-tracking-service': '^2.0.0',
    },
  },

  create: ({ featureServices, featureAppId }) => {
    const loggerService = featureServices['s2:logger'];
    const contentService = featureServices['audi-content-service'];
    const localeService = featureServices['locale-service'];
    const vueFormatterService = featureServices['dbad:audi-vue-formatter-service'];

    const providerfootnoteService = featureServices[
      'audi-footnote-reference-service'
    ] as unknown as AudiFootnoteRefernceServiceScopeManagerInterfaceV3;
    console.log('🚀 ~ providerfootnoteService:', providerfootnoteService);

    const footnoteReferenceService: AudiFootnoteReferenceServiceInterfaceV3 =
      providerfootnoteService?.getDefaultScopeRefService();

    // SSR
    // --------------------------------------------------------------------
    const asyncSSRManager = featureServices['s2:async-ssr-manager'];
    const serializedStateManager = featureServices['s2:serialized-state-manager'];
    // on the server
    if (asyncSSRManager) {
      asyncSSRManager.scheduleRerender(
        (async () => {
          const content = await contentService?.getContent();
          serializedStateManager?.register(() => JSON.stringify(content));
        })(),
      );
    }

    return {
      render: () => {
        return (
          <InViewContextProvider logger={loggerService}>
            <UniversalEditorProvider
              contentService={featureServices['audi-content-service']!}
              renderModeService={featureServices['audi-render-mode-service']}
            >
              <FootnoteContextProvider footnoteReferenceService={footnoteReferenceService}>
                <ContextProvider
                  featureAppId={featureAppId}
                  localeService={localeService}
                  vueFormatterService={vueFormatterService!}
                  contentService={contentService!}
                  footnoteReferenceService={providerfootnoteService}
                  loggerService={loggerService}
                >
                  <div data-fefa-custom-id={footnoteReferenceService?.getConsumerId()}>
                    <FeatureApp
                      trackingService={featureServices['audi-tracking-service']}
                      loggerService={loggerService}
                      localeService={localeService}
                    />
                  </div>
                </ContextProvider>
              </FootnoteContextProvider>
            </UniversalEditorProvider>
          </InViewContextProvider>
        );
      },
    };
  },
};

export default featureAppDefinition;
